/**
 * API functions for review progress tracking
 */

import apiClient from './client';
import {
  ReviewProgressCreate,
  ReviewProgressUpdate,
  ReviewProgressResponse,
  ReviewSessionSummary,
  CoverageResponse,
  NextUnreviewedResponse,
  UserReviewStats,
} from '@/types/api';

/**
 * Start a new review session
 */
export const startReviewSession = async (
  data: ReviewProgressCreate
): Promise<ReviewProgressResponse> => {
  const response = await apiClient.post<ReviewProgressResponse>(
    '/review-progress/start',
    data
  );
  return response.data;
};

/**
 * Get all active review sessions for current user
 */
export const getActiveSessions = async (
  chatId?: number
): Promise<ReviewSessionSummary[]> => {
  const response = await apiClient.get<ReviewSessionSummary[]>(
    '/review-progress/active',
    {
      params: chatId ? { chat_id: chatId } : undefined,
    }
  );
  return response.data;
};

/**
 * Get details of a specific review session
 */
export const getSession = async (
  sessionId: number
): Promise<ReviewProgressResponse> => {
  const response = await apiClient.get<ReviewProgressResponse>(
    `/review-progress/session/${sessionId}`
  );
  return response.data;
};

/**
 * Update progress for a review session
 */
export const updateSessionProgress = async (
  sessionId: number,
  data: ReviewProgressUpdate
): Promise<{ status: string; session_id: number; progress_percentage: number }> => {
  const response = await apiClient.post(
    `/review-progress/session/${sessionId}/update`,
    data
  );
  return response.data;
};

/**
 * Mark a review session as complete
 */
export const completeSession = async (
  sessionId: number
): Promise<{
  status: string;
  session_id: number;
  total_messages: number;
  reviewed_messages: number;
  accepted_count: number;
  rejected_count: number;
  progress_percentage: number;
}> => {
  const response = await apiClient.post(
    `/review-progress/session/${sessionId}/complete`
  );
  return response.data;
};

/**
 * Get coverage statistics for a library section
 */
export const getSectionCoverage = async (
  sectionId: number
): Promise<CoverageResponse> => {
  const response = await apiClient.get<CoverageResponse>(
    `/review-progress/section/${sectionId}/coverage`
  );
  return response.data;
};

/**
 * Get next unreviewed message range for smart navigation
 */
export const getNextUnreviewed = async (
  chatId: number,
  librarySectionId?: number,
  preferredSize: number = 2000
): Promise<NextUnreviewedResponse> => {
  const response = await apiClient.get<NextUnreviewedResponse>(
    `/review-progress/chat/${chatId}/next-unreviewed`,
    {
      params: {
        library_section_id: librarySectionId,
        preferred_size: preferredSize,
      },
    }
  );
  return response.data;
};

/**
 * Get current user's review statistics
 */
export const getUserReviewStats = async (): Promise<UserReviewStats> => {
  const response = await apiClient.get<UserReviewStats>(
    '/review-progress/user/stats'
  );
  return response.data;
};
